import { z } from "zod";
import { SharedBotInputSchema, SharedBotUpdateToolSchema } from "./shared-bots.js";

const key = z.string().trim().min(1).max(100);
const text = z.string().trim().min(1).max(16000);
export const MissionStatusSchema = z.enum([
  "draft",
  "running",
  "waiting",
  "paused",
  "failed",
  "completed",
  "cancelled",
]);
export type MissionStatus = z.infer<typeof MissionStatusSchema>;
export const MissionAssignmentStatusSchema = z.enum([
  "queued",
  "running",
  "waiting",
  "completed",
  "failed",
  "cancelled",
]);
export type MissionAssignmentStatus = z.infer<typeof MissionAssignmentStatusSchema>;
export const MissionEvidenceKindSchema = z.enum([
  "code",
  "check",
  "preview",
  "visual",
  "artifact",
  "finding",
  "release",
]);
export type MissionEvidenceKind = z.infer<typeof MissionEvidenceKindSchema>;
export const MissionRevisionsSchema = z.record(key, z.string().trim().min(1).max(250));
export const MissionCriterionSchema = z
  .object({
    key,
    description: text,
    evidenceKinds: z.array(MissionEvidenceKindSchema).min(1).max(7),
    release: z
      .object({
        action: z.enum(["merge", "deploy"]),
        repository: z.string().regex(/^[A-Za-z0-9_-]+\/[A-Za-z0-9_.-]+$/),
        destination: key,
      })
      .strict()
      .nullable()
      .default(null),
    requiredChecks: z.array(z.string().trim().min(1).max(250)).max(100).default([]),
    sourceKeys: z.array(key).max(30).default([]),
  })
  .strict();
export type MissionCriterion = z.infer<typeof MissionCriterionSchema>;
export const MissionAssignmentPlanSchema = z
  .object({
    key,
    title: z.string().trim().min(1).max(200),
    objective: text,
    executor: z.enum(["fleet", "cloud_agent", "shared_guide"]),
    botId: key.nullable().default(null),
    model: z
      .object({
        provider: key,
        modelId: z.string().trim().min(1).max(250),
        reason: z.string().trim().min(1).max(1000),
      })
      .strict()
      .nullable()
      .default(null),
    environment: z
      .discriminatedUnion("kind", [
        z.object({ kind: z.literal("cloud") }).strict(),
        z.object({ kind: z.literal("device"), deviceId: key }).strict(),
      ])
      .nullable()
      .default(null),
    repositories: z
      .array(z.string().regex(/^[A-Za-z0-9_-]+\/[A-Za-z0-9_.-]+$/))
      .max(100)
      .default([]),
    integrationIds: z.array(key).max(100).default([]),
    contextSources: z
      .array(z.string().min(1).max(2000))
      .max(50)
      .default([])
      .describe(
        "Exact id or url values from mission.sources. Do not use labels, ticket identifiers, repository names, or sourceRevisions keys unless they equal an attached source id or url.",
      ),
    repositorySourceKey: key
      .nullable()
      .default(null)
      .describe(
        "A key in plan.sourceRevisions identifying this assignment's repository; distinct from contextSources.",
      ),
    repositorySourceKind: z
      .enum(["pull_request", "commit"])
      .optional()
      .describe(
        "Defaults to pull_request. Use commit only for a fleet source audit with an exact SHA in sourceRevisions; read files with mission_repository_file. Commit observations cannot verify checks, previews, or releases.",
      ),
    evidence: z
      .array(z.object({ criterionKey: key, kind: MissionEvidenceKindSchema }).strict())
      .max(100)
      .default([]),
    dependsOn: z.array(key).max(100).default([]),
    required: z.boolean().default(true),
  })
  .strict();
export type MissionAssignmentPlan = z.infer<typeof MissionAssignmentPlanSchema>;
export const MissionPolicySchema = z
  .object({
    maxParallel: z.number().int().min(1).max(100).default(4),
    maxAttempts: z.number().int().min(1).max(10).default(3),
    budgetUsd: z.number().finite().positive().nullable().default(null),
    reviewEvidence: z.boolean().default(false),
    allowMerge: z.boolean().default(false),
    allowDeploy: z.boolean().default(false),
    repositoryScope: z
      .array(z.string().regex(/^[A-Za-z0-9_-]+\/[A-Za-z0-9_.-]+$/))
      .max(100)
      .default([]),
  })
  .strict();
export const MissionPlanSchema = z
  .object({
    changeSummary: z.string().trim().max(4000).default(""),
    consolidatedDirections: z
      .object({
        throughSequence: z.number().int().positive().safe(),
        summary: z.string().trim().min(1).max(16000),
      })
      .strict()
      .optional()
      .describe(
        "Owner-approved summary replacing owner directions through this mission event sequence in future run context. Preserve all applicable constraints and corrections. Original events remain in history; later directions are still included. Only the owner may change this summary or cutoff.",
      ),
    criteria: z.array(MissionCriterionSchema).min(1).max(100),
    assignments: z.array(MissionAssignmentPlanSchema).min(1).max(100),
    sourceRevisions: MissionRevisionsSchema.default({}).describe(
      "Every criterion.sourceKeys and assignment.repositorySourceKey must exist here. Use the observed revision, or uncollected for a repository whose commit has not been collected yet. The repository collector replaces it with the actual PR head; never invent a commit.",
    ),
    policy: MissionPolicySchema.default(() => MissionPolicySchema.parse({})),
  })
  .strict();
export type MissionPlan = z.infer<typeof MissionPlanSchema>;
export const CreateMissionSchema = z
  .object({
    operationKey: key,
    projectId: key.nullable().default(null),
    title: z.string().trim().min(1).max(200),
    objective: text,
  })
  .strict();
export type CreateMission = z.infer<typeof CreateMissionSchema>;

/** Normalized evidence from a server collector or an agent report; provenance is server-owned. */
export const MissionEvidenceInputSchema = z
  .object({
    operationKey: key,
    assignmentKey: key,
    criterionKey: key.nullable().default(null),
    kind: MissionEvidenceKindSchema,
    verdict: z.enum(["observed", "passed", "failed"]),
    sourceRevisions: MissionRevisionsSchema.default({}),
    reference: z
      .string()
      .trim()
      .min(1)
      .max(2000)
      .describe(
        "Source reference. For a saved review or log file, use artifact:<id> returned by attach_file; its text is included in acceptance review. Local file paths alone are not readable evidence.",
      ),
    summary: z.string().max(4000).default(""),
  })
  .strict();
export type MissionEvidenceInput = z.infer<typeof MissionEvidenceInputSchema>;
export type MissionEvidenceRecord = MissionEvidenceInput & {
  id: string;
  planRevision: number;
  provenance: "collector" | "agent";
  stale: boolean;
};
export const MissionDecisionTargetSchema = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("repository"),
      repository: z.string().regex(/^[A-Za-z0-9_-]+\/[A-Za-z0-9_.-]+$/),
      pullNumber: z.number().int().positive(),
      headSha: key,
      baseBranch: key,
      baseSha: z
        .string()
        .regex(/^[a-f0-9]{40,64}$/)
        .nullable()
        .default(null),
    })
    .strict(),
  z
    .object({ kind: z.literal("deployment"), project: key, environment: key, revision: key })
    .strict(),
  z
    .object({
      kind: z.literal("shared_guide"),
      botId: key,
      revision: z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .nullable(),
      settings: SharedBotInputSchema.omit({ password: true }).strict(),
    })
    .strict(),
]);
export const MissionDecisionInputSchema = z
  .object({
    supersedesId: key.nullable().default(null),
    assignmentKey: key.nullable().default(null),
    target: MissionDecisionTargetSchema.nullable().default(null),
    operationKey: key,
    title: z.string().trim().min(1).max(200),
    action: z.enum(["question", "review", "merge", "deploy", "publish_guide"]),
    details: text,
    sourceRevisions: MissionRevisionsSchema.default({}),
  })
  .strict();
export type MissionDecisionInput = z.infer<typeof MissionDecisionInputSchema>;

/** Proposals preserve omitted settings; passwords and share tokens never enter a mission. */
export const MissionGuideProposalInput = z
  .object({
    id: key,
    version: z.number().int().nonnegative(),
    assignmentKey: key,
    botId: key.nullable().default(null),
    expectedVersion: z
      .string()
      .regex(/^[a-f0-9]{64}$/)
      .nullable()
      .default(null),
    settings: SharedBotUpdateToolSchema.shape.settings,
    supersedesId: key.nullable().default(null),
  })
  .strict();
export const MissionGuideApplyInput = z.object({ id: key, decisionId: key }).strict();
